import {
  PvInverter,
  Site,
} from "@inrange/building-manager-api-client/models-site";
import isSiteOwnerOccupied from "../../../theme-components/src/utils/isSiteOwnerOccupied";
import {
  inverterRatedOutputPowerTotalKwpAc,
  roundToDp,
} from "../routes/site/siteSections/utils";

export const csvHeaders = [
  // Ownership data
  { label: "OWNERSHIP", key: "headingOwnership" },
  { label: "OO Org ID", key: "ooOrgId" },
  { label: "OO Org Name", key: "ooOrgName" },
  { label: "Tenant Org ID", key: "tenantOrgId" },
  { label: "Tenant Org Name", key: "tenantOrgName" },
  { label: "Landlord Org ID", key: "landlordOrgId" },
  { label: "Landlord Org Name", key: "landlordOrgName" },
  // This site data
  { label: "SITE DATA", key: "headingSiteData" },
  { label: "Org ID", key: "orgID" },
  { label: "Site Org Link", key: "siteOrgLinkType" },
  { label: "SDM excluded from matching", key: "sdmExcludeFromMatching" },
  { label: "SDM hide matches", key: "sdmHideMatches" },
  { label: "Site ID", key: "id" },
  { label: "Name", key: "name" },
  { label: "Address", key: "address" },
  { label: "Country Code", key: "countryCode" },
  { label: "Currency", key: "currencyCode" },
  { label: "Operational Status", key: "operationalStatus" },
  // Demand
  { label: "DEMAND", key: "headingDemand" },
  { label: "Building Area", key: "totalBuildingArea" },
  { label: "Roof Utilisation", key: "roofUtilization" },
  { label: "Demand Data Source", key: "demandDataSource" },
  { label: "Annual Demand (kWh)", key: "demand" },
  { label: "Building profile", key: "buildingProfile" },
  { label: "Existing PV", key: "existingPv" },
  // Generation
  { label: "GENERATION", key: "headingGeneration" },
  { label: "Initial cost/kWp", key: "initialCostPerKWp" },
  { label: "Installed Capacity", key: "installedCapacity" },
  { label: "AC Capacity", key: "totalKwpAc" },
  { label: "Annual Generation (kWh)", key: "generation" },
  // Energy flow
  { label: "ENERGY FLOW", key: "headingEnergyFlow" },
  { label: "Behind Meter", key: "behindMeter" },
  { label: "Curtailed", key: "curtailed" },
  { label: "Network Export", key: "networkExport" },
  { label: "Spill Export", key: "exported" },
  { label: "Network Import", key: "networkImport" },
  { label: "Grid Import", key: "gridImport" },
  { label: "Battery Grid Discharge", key: "batteryGridDischarge" },
  // Tariffs
  { label: "TARIFFS", key: "headingTariffs" },
  { label: "Tenant Tariff", key: "tenantTariff" },
  { label: "Network Import Tariff", key: "networkImportTariff" },
  { label: "Market Tariff", key: "marketTariff" },
  { label: "OffPeak Tariff", key: "offPeakTariff" },
  { label: "InRange Export Tariff", key: "inrangeExportTariff" },
  { label: "InRange Actual Export Tariff", key: "inrangeActualExportTariff" },
  // Customer License Financial performance
  {
    label: "CUSTOMER LICENSE PERFORMANCE",
    key: "headingCustomerLicensePerformance",
  },
  { label: "Landlord License IRR", key: "landlordIRR" },
  { label: "Landlord License Payback", key: "landlordPaybackMonths" },
  { label: "Owner Occupier License IRR", key: "ownerOccupierIRR" },
  {
    label: "Owner Occupier License Payback",
    key: "ownerOccupierPaybackMonths",
  },
  // InRange Financial performance
  { label: "INRANGE PERFORMANCE", key: "headingInrangePerformance" },
  { label: "Active Investment Model", key: "activeInvestmentModel" },
  { label: "InRange upfront arbitrage", key: "inrangeUpfrontArbitrage" },
  { label: "InRange net ARR", key: "inrangeNetARR" },
  { label: "InRange Lease IRR", key: "inrangeLeaseIRR" },
  { label: "InRange Lease Payback", key: "inrangeLeasePaybackMonths" },
  // Network data
  { label: "NETWORK DATA", key: "headingNetworkData" },
  { label: "DNO", key: "dno" },
  { label: "Substation", key: "substation" },
  { label: "Max forecast export", key: "maxExport" },
  { label: "Export limit / Generation Headroom (kVA)", key: "headroom" },
  { label: "Export Limit Type", key: "exportLimitType" },
  // Tenant energy share
  { label: "TENANT ENERGY SHARE", key: "headingTenantEnergyShare" },
  { label: "Annual Tenant Share", key: "annualTenantShare" },
  { label: "Monthly Tenant Share Jan", key: "monthlyTenantShareJan" },
  { label: "Monthly Tenant Share Feb", key: "monthlyTenantShareFeb" },
  { label: "Monthly Tenant Share Mar", key: "monthlyTenantShareMar" },
  { label: "Monthly Tenant Share Apr", key: "monthlyTenantShareApr" },
  { label: "Monthly Tenant Share May", key: "monthlyTenantShareMay" },
  { label: "Monthly Tenant Share Jun", key: "monthlyTenantShareJun" },
  { label: "Monthly Tenant Share Jul", key: "monthlyTenantShareJul" },
  { label: "Monthly Tenant Share Aug", key: "monthlyTenantShareAug" },
  { label: "Monthly Tenant Share Sep", key: "monthlyTenantShareSep" },
  { label: "Monthly Tenant Share Oct", key: "monthlyTenantShareOct" },
  { label: "Monthly Tenant Share Nov", key: "monthlyTenantShareNov" },
  { label: "Monthly Tenant Share Dec", key: "monthlyTenantShareDec" },
];

const getSiteOwnership = (siteOwnershipByType) => {
  return [
    siteOwnershipByType["ownerOccupier-id"] || "Unknown",
    siteOwnershipByType["ownerOccupier-name"] || "Unknown",
    siteOwnershipByType["tenant-id"] || "Unknown",
    siteOwnershipByType["tenant-name"] || "Unknown",
    siteOwnershipByType["landlord-id"] || "Unknown",
    siteOwnershipByType["landlord-name"] || "Unknown",
  ];
};

export const formatCsvRow = (
  site: Site,
  orgID: string,
  siteOrgLinkType: string,
  inverterSpecs: Record<string, PvInverter>
) => {
  const addOwnership = (acc, ownershipType, ownership) => {
    if (acc[`${ownershipType}-id`]) {
      acc[`${ownershipType}-id`] += `;${ownership.orgID}`;
    } else {
      acc[`${ownershipType}-id`] = ownership.orgID;
    }
    if (acc[`${ownershipType}-name`]) {
      acc[`${ownershipType}-name`] += `;${ownership.name}`;
    } else {
      acc[`${ownershipType}-name`] = ownership.name;
    }
  };

  const siteOwnershipByType = site.siteOwnerships.reduce((acc, ownership) => {
    addOwnership(acc, ownership.ownership, ownership);
    if (ownership.ownership === "ownerOccupier") {
      addOwnership(acc, "landlord", ownership);
      addOwnership(acc, "tenant", ownership);
    }
    return acc;
  }, {});
  const [
    ooOrgId,
    ooOrgName,
    tenantOrgId,
    tenantOrgName,
    landlordOrgId,
    landlordOrgName,
  ] = getSiteOwnership(siteOwnershipByType);

  const totalKwpAc = inverterRatedOutputPowerTotalKwpAc(
    inverterSpecs,
    site.totalInverters
  );

  const ownershipKey = isSiteOwnerOccupied(site.siteOwnerships)
    ? "ownerOccupier"
    : "landlord";

  const monthlyTenantEnergyShare = (monthIndex: number) => {
    return site.energyFlowMonthly[monthIndex].generation > 0
      ? roundToDp(
          site.energyFlowMonthly[monthIndex].behindMeter /
            site.energyFlowMonthly[monthIndex].generation,
          4
        )
      : 0;
  };

  return {
    // Ownership data
    headingOwnership: "",
    ooOrgId,
    ooOrgName,
    tenantOrgId,
    tenantOrgName,
    landlordOrgId,
    landlordOrgName,
    // This site data
    headingSiteData: "",
    orgID: orgID,
    siteOrgLinkType: siteOrgLinkType,
    sdmExcludeFromMatching: site.sdmExcludeFromMatching,
    sdmHideMatches: site.sdmHideMatches,
    id: site.id,
    name: site.name,
    address: site.address,
    countryCode: site.countryCode,
    currencyCode: site.currencyCode,
    operationalStatus: site.operationalStatus,
    // Demand
    headingDemand: "",
    totalBuildingArea: site.totalBuildingArea,
    roofUtilization: site.totalRoofUtil,
    demandDataSource: [
      ...Object.values(site.demand)
        .map((d) => d.halfHourlyDemand?.source)
        .reduce((acc, curr) => acc.add(curr), new Set()),
    ].join(","),
    demand: site.energyFlowAnnual.demand,
    buildingProfile: [
      ...Object.values(site.demand)
        .map((d) => d.buildingProfile)
        .reduce((acc, curr) => acc.add(curr), new Set()),
    ].join(","),
    existingPv: [
      ...Object.values(site.demand).map((d) => d.existingPvKwp ?? ""),
    ].join(","),
    // Generation
    headingGeneration: "",
    initialCostPerKWp: site.projectCosts.initialCostPerKWp,
    installedCapacity: site.totalInstalledCapacity,
    totalKwpAc: totalKwpAc,
    generation: site.energyFlowAnnual.generation,
    // Energy flow
    headingEnergyFlow: "",
    behindMeter: site.energyFlowAnnual.behindMeter,
    networkExport: site.energyFlowAnnual.networkExport,
    exported: site.energyFlowAnnual.exported,
    curtailed: site.energyFlowAnnual.curtailed,
    networkImport: site.energyFlowAnnual.networkImport,
    gridImport: site.energyFlowAnnual.gridImport,
    batteryGridDischarge: site.energyFlowAnnual.batteryGridDischarge,
    // Tariffs
    headingTariffs: "",
    tenantTariff: site.tariffConfig.onsitePPA.tariff,
    networkImportTariff: site.tariffConfig.networkImportTariff,
    marketTariff:
      site.tariffConfig.supplier.peakOffPeak.overrideRatesMap["peak"].tariff,
    offPeakTariff: site.tariffConfig.supplier.peakOffPeak.baseRate.tariff,
    inrangeExportTariff: site.tariffConfig.spillExportPPA.tariff,
    inrangeActualExportTariff: site.tariffConfig.actualSpillTariff,
    // Customer License Financial performance
    headingCustomerLicensePerformance: "",
    landlordIRR: site.financialModels.landlord.license.irr,
    landlordPaybackMonths: site.financialModels.landlord.license.paybackMonths,
    ownerOccupierIRR: site.financialModels.ownerOccupier.license.irr,
    ownerOccupierPaybackMonths:
      site.financialModels.ownerOccupier.license.paybackMonths,
    // InRange Financial performance
    headingInrangePerformance: "",
    activeInvestmentModel: site.activeInvestmentModel,
    inrangeUpfrontArbitrage: site.financialModelsInrange.installArbitrage,
    inrangeNetARR:
      site.activeInvestmentModel === "lease"
        ? site.financialModelsInrange.lease.netRevenue
        : site.financialModels[ownershipKey].license.inrangeNetRevenue,
    inrangeLeaseIRR: site.financialModelsInrange.lease.irr,
    inrangeLeasePaybackMonths: site.financialModelsInrange.lease.paybackMonths,
    // Network data
    headingNetworkData: "",
    dno: site.dno?.name,
    substation: site.substationData?.name,
    maxExport: site.energyFlowAnnual.maxExport,
    headroom:
      site.totalExportLimit !== undefined && site.totalExportLimit !== null
        ? site.totalExportLimit
        : (site.substationData?.headroom ?? 0) * 1000,
    exportLimitType:
      site.totalExportLimit !== undefined && site.totalExportLimit !== null
        ? "Export Limit"
        : "Headroom",
    // Tenant energy share
    headingTenantEnergyShare: "",
    annualTenantShare: site.energyFlowAnnual.tenantEnergyShare,
    monthlyTenantShareJan: monthlyTenantEnergyShare(1),
    monthlyTenantShareFeb: monthlyTenantEnergyShare(2),
    monthlyTenantShareMar: monthlyTenantEnergyShare(3),
    monthlyTenantShareApr: monthlyTenantEnergyShare(4),
    monthlyTenantShareMay: monthlyTenantEnergyShare(5),
    monthlyTenantShareJun: monthlyTenantEnergyShare(6),
    monthlyTenantShareJul: monthlyTenantEnergyShare(7),
    monthlyTenantShareAug: monthlyTenantEnergyShare(8),
    monthlyTenantShareSep: monthlyTenantEnergyShare(9),
    monthlyTenantShareOct: monthlyTenantEnergyShare(10),
    monthlyTenantShareNov: monthlyTenantEnergyShare(11),
    monthlyTenantShareDec: monthlyTenantEnergyShare(12),
  };
};
